import React, { useEffect } from 'react';
import { Center } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { matchPath, Outlet, useNavigate } from 'react-router-dom';

import { useReduxDispatch } from '../../../app/store';
import { useOnboardingWizardProgress } from '../../home/useOnboardingWizardProgress';
import { selectEvChargers, selectSite, setCurrentSite } from '../../site/siteSlice';
import { useEVSEStream } from '../../smart-amp/useSmartAmpSse';
import { syncDetectedEvseModel } from '../device-setup/deviceSetupSlice';
import { resolveEvseVariant } from '../evse-variant';
import { setEvseVariant, setSiteDeviceId } from './evChargerSlice';

/* istanbul ignore next -- @preserve */
export const EVChargerHome = () => {
  const evChargers = useSelector(selectEvChargers);
  const hasEvChargers = !!evChargers?.length;
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();
  const isOnboardingLoading = useOnboardingWizardRedirect();

  // Own the SSE stream here, at the parent route wrapper, so it stays open across all child
  // routes (device-status, device-details, device-charging-schedule, smart-amp, …) instead of
  // being aborted and reopened on every sub-navigation. Child pages read the resulting status
  // from Redux via useSmartAmpSse/selectEvChargerData. Guards on chargerId, so it's a no-op
  // during pre-enrolment onboarding routes.
  useEVSEStream();

  useEffect(() => {
    if (!hasEvChargers) return;

    const evChargerId = evChargers[0].id;
    if (evChargerId) dispatch(setSiteDeviceId(evChargerId.toString()));
    // The site device model is the `model_number` persisted after Soft-AP detection.
    dispatch(setEvseVariant(resolveEvseVariant(evChargers[0].model)));
    // Retries a model save that failed in an earlier session.
    dispatch(syncDetectedEvseModel());
  }, [dispatch, evChargers, hasEvChargers]);

  useEffect(() => {
    if (isOnboardingLoading) return;
    const isMatch = matchPath('/sites/:id/home', location.pathname);
    if (isMatch) {
      const path = !hasEvChargers ? 'device-register' : 'device-status';
      navigate(path, { replace: true });
    }
  }, [hasEvChargers, navigate, isOnboardingLoading]);

  return (
    <Center flexDirection={'column'} height="100%">
      <Outlet />
    </Center>
  );
};

/**
 * Responsible for handling various cases of user site or account configuration required before they can use the app
 * for their site.
 * The order these steps should appear for system owners is as follows:
 *
 * 1. Redirect the user to set the ZIP code on their site, if it does not yet exist
 * 2. Get the user's consent requirements for data sharing with customer support
 * 3. Ask the user which hardware they've installed in their home
 *
 * @returns Whether onboarding progress is still loading.
 */
function useOnboardingWizardRedirect() {
  const navigate = useNavigate();
  const {
    needsSiteAddress,
    needsToConfigureHardware,
    needsToProvideCustomerSupportConsent,
    needsToVisitTariffs,
    isLoaded,
  } = useOnboardingWizardProgress();
  const site = useSelector(selectSite);
  const dispatch = useReduxDispatch();

  useEffect(() => {
    if (isLoaded) {
      // needsToVisitTariffs = site.isOnboarding. Including it prevents a premature reset
      // right after charger enrolment: addEvCharger sets devices.length > 0 (clearing
      // needsToConfigureHardware) while the user is still in the WiFi/tariff flow.
      // The site.isOnboarding guard prevents an infinite dispatch loop — without it the
      // effect would repeatedly call setCurrentSite, changing site, retriggering the effect.
      const needsToCompleteOnboarding =
        needsSiteAddress || needsToConfigureHardware || needsToProvideCustomerSupportConsent || needsToVisitTariffs;
      if (!needsToCompleteOnboarding && site.isOnboarding) {
        dispatch(setCurrentSite({ ...site, isOnboarding: false }));
      }
    }
  }, [
    dispatch,
    isLoaded,
    needsSiteAddress,
    needsToConfigureHardware,
    needsToProvideCustomerSupportConsent,
    needsToVisitTariffs,
    site,
  ]);

  useEffect(() => {
    if (isLoaded) {
      if (needsSiteAddress) {
        navigate('../update');
      } else if (needsToProvideCustomerSupportConsent) {
        navigate('../set_customer_support_consent');
      }
      // needsToConfigureHardware is handled by EVChargerHome's redirect to device-register
    }
  }, [isLoaded, needsSiteAddress, needsToProvideCustomerSupportConsent, navigate]);

  return !isLoaded;
}
